/**
 * Helpers deep-link séjour / post-login (ticket #435).
 */

/**
 * Remappe les chemins backoffice pro (`/app/stay/:id`, `/app/booking/:id`)
 * vers Mes réservations côté app client.
 */
export function mapProBookingPathToClientReservations(
  path: string | null | undefined
): string | null {
  if (!path) return null;
  const stay = path.match(/^\/app\/stay\/(\d+)\/?$/);
  if (stay) return `/reservations?booking_id=${stay[1]}`;
  const booking = path.match(/^\/app\/booking\/(\d+)\/?$/);
  if (booking) return `/reservations?booking_id=${booking[1]}`;
  return null;
}

/** Refuse open-redirect : chemins relatifs internes uniquement. */
export function sanitizeClientRedirectPath(
  raw: string | null | undefined,
  fallback = '/'
): string {
  if (!raw || !raw.trim()) return fallback;
  const trimmed = raw.trim();

  try {
    if (/^https?:\/\//i.test(trimmed)) {
      const url = new URL(trimmed);
      if (typeof window !== 'undefined' && url.origin !== window.location.origin) {
        return fallback;
      }
      return sanitizeClientRedirectPath(`${url.pathname}${url.search}${url.hash}`, fallback);
    }
  } catch {
    return fallback;
  }

  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return fallback;
  }
  return trimmed;
}

export type StayDeepLinkAction = 'highlight' | 'reschedule_modal' | null;

export interface DeepLinkBookingLike {
  bookingId: number;
  status: string;
  type?: string;
  rescheduleProposedBy?: string;
}

/** Décide l'action UI pour ?booking_id= sur Mes Réservations. */
export function resolveStayDeepLinkAction(
  booking: DeepLinkBookingLike | undefined
): StayDeepLinkAction {
  if (!booking) return null;
  if (
    booking.status === 'pending_reschedule' &&
    booking.rescheduleProposedBy === 'pro'
  ) {
    return 'reschedule_modal';
  }
  if (booking.status === 'pending_client') {
    return 'highlight';
  }
  return 'highlight';
}

/** Adapte une réponse hosting plate au shape BookingConfirmed. */
export function mapHostingConfirmationBooking(raw: Record<string, unknown> | null | undefined) {
  if (!raw) return null;
  if (raw.service && typeof raw.service === 'object' && raw.bookingId != null) {
    return raw as {
      bookingId: number;
      service: { serviceName: string; duration: number };
      date: string;
      startTime: string;
      pro: { name: string };
      animal: { name: string };
      status: string;
      locationName?: string;
      locationAddress?: string;
      serviceAddress?: string;
    };
  }

  const start = raw.start_date ?? raw.date;
  const date =
    start && typeof (start as { toISOString?: () => string }).toISOString === 'function'
      ? String((start as { toISOString: () => string }).toISOString()).slice(0, 10)
      : String(start || '');

  const checkin =
    raw.specific_checkin_time || raw.checkin_time || raw.pool_checkin_start || raw.startTime || '14:00';

  return {
    bookingId: Number(raw.stay_booking_id ?? raw.bookingId),
    service: {
      serviceName: String(raw.service_name || 'Hébergement'),
      duration: 0,
    },
    date,
    startTime: String(checkin).slice(0, 5),
    pro: { name: String(raw.pro_name || (raw.pro as { name?: string })?.name || '') },
    animal: {
      name: String(raw.animal_name || (raw.animal as { name?: string })?.name || ''),
    },
    status: String(raw.status || ''),
    locationName: (raw.unit_name || raw.zone_name || raw.locationName) as string | undefined,
    locationAddress: raw.locationAddress as string | undefined,
    serviceAddress: raw.serviceAddress as string | undefined,
  };
}
